import { Box, Paper, Stack, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useContext, useMemo, useState } from 'react';

import { UserContext } from '~/auth/userContext';
import { InvoiceActions, InvoiceTabContainer } from '~/components/invoices';
import { AiReviewSummary } from '~/components/invoices/detailPage/AiReviewSummary';
import { CreditAppliesToCard } from '~/components/invoices/detailPage/CreditAppliesToCard';
import { InvoiceCreditsCard } from '~/components/invoices/detailPage/InvoiceCreditsCard';
import { InvoiceDetailPageHeader } from '~/components/invoices/detailPage/InvoiceDetailPageHeader';
import { InvoiceDocumentDialog } from '~/components/invoices/detailPage/InvoiceDocumentDialog';
import {
  getPrimaryDocuments,
  getSupportingDocuments,
} from '~/components/invoices/detailPage/invoiceDocuments';
import { InvoiceDocumentViewer } from '~/components/invoices/detailPage/InvoiceDocumentViewer';
import { useFinalDecision } from '~/components/invoices/finalDecision/useFinalDecision';
import InvoiceDialog from '~/components/invoices/InvoiceDialog';
import { DecisionSection } from '~/components/invoices/InvoiceOverview';
import { useInvoiceReviewActions } from '~/components/invoices/useInvoiceReviewActions';
import { mapInvoiceToFormValues } from '~/components/uploadDocument/InvoiceForm';
import type { Invoice } from '~/data/schemas/invoice';
import { invoiceKindToLabelMap, isCredit, isInvoiceFinal } from '~/data/schemas/invoice';
import { DetailTableHeightProvider } from '~/hooks/useDetailTableHeight';
import { CARD_SX, pageBandColor } from '~/theme';
import { ACTIONS_ON_SPENDING, canAccess } from '~/utils/role';

/** The buttons need a narrow column; the Decision card that replaces them needs more. */
const ACTIONS_WIDTH = 170;
const DECISION_WIDTH = 340;

/** Height the pinned mobile action bar reserves, so the last row of content can still be scrolled clear of it. */
const MOBILE_ACTION_BAR_HEIGHT = 64;

interface InvoiceDetailPageProps {
  invoice: Invoice | undefined;
  isLoading: boolean;
  /** `null` in a dialog, which closes from its own title bar. */
  onBack: (() => void) | null;
  backLabel?: string;
  variant?: 'page' | 'preview';
}

/**
 * Full-page invoice/proposal detail: header (identity and document link on the left, AI
 * verdict and review decision on the right) over the full-width detail tabs. The
 * document opens in a dialog rather than holding a permanent side pane it mostly
 * wasn't using.
 */
export function InvoiceDetailPage({
  invoice,
  isLoading,
  onBack,
  backLabel,
  variant = 'page',
}: InvoiceDetailPageProps) {
  const isPreview = variant === 'preview';
  const [documentOpen, setDocumentOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { currencyCode, userDetails } = useContext(UserContext);
  // `InvoiceActions` renders its toolbar either way, so without this gate a reader with
  // no decision to make gets an empty bar pinned across the bottom.
  const canDecide = canAccess(userDetails.role, ACTIONS_ON_SPENDING);
  const theme = useTheme();
  // Below `md` the header has no room for a decision column beside the invoice, so the
  // decision moves to the bottom of the page.
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) && !isPreview;

  const { invoiceActions, revalidateNotes, tickets, disputeAgent } =
    useInvoiceReviewActions(invoice);

  const primaryDocuments = useMemo(() => getPrimaryDocuments(invoice), [invoice]);
  const supportingDocuments = useMemo(
    () => getSupportingDocuments(invoice, tickets),
    [invoice, tickets],
  );
  const hasDocument = primaryDocuments.length > 0 || supportingDocuments.length > 0;

  // Once a decision is made the buttons collapse to a single Undo Final Stamp, which
  // goes inside the Decision card instead of standing alone.
  // Credits are filed approved but have no decision to stamp or undo.
  const credit = invoice ? isCredit(invoice) : false;
  const isFinal = invoice && !credit ? isInvoiceFinal(invoice) : false;

  // The invoice's own fields while it is still open, and the stamped decision once it
  // isn't. Both forms revalidate this invoice, so the page picks the edit up.
  const invoiceFormData = useMemo(
    () => (invoice ? mapInvoiceToFormValues(invoice) : null),
    [invoice],
  );
  const closeEdit = useCallback(() => setEditOpen(false), []);
  // Always called: a hook can't be conditional. A null id holds its request until the
  // form is open on a finalized invoice.
  const finalDecisionForm = useFinalDecision(
    editOpen && isFinal ? (invoice?.id ?? null) : null,
    closeEdit,
  );
  // `variant="footer"` is the drawer's full-width bar, the shape the pinned mobile bar
  // needs. Rendered once either way: `InvoiceActions` owns the decision dialogs.
  const actions = (
    <InvoiceActions
      {...invoiceActions}
      invoice={invoice}
      revalidateNotes={revalidateNotes}
      variant={isPreview ? 'inline' : isMobile ? 'footer' : 'header'}
    />
  );

  const actionsWidth = isFinal || credit ? DECISION_WIDTH : ACTIONS_WIDTH;

  const decisionCard =
    isFinal && invoice ? (
      <DecisionSection
        invoice={invoice}
        currencyCode={currencyCode}
        layout={isPreview ? 'compact' : 'header'}
        action={actions}
      />
    ) : null;

  return (
    <DetailTableHeightProvider>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100%"
        bgcolor={isPreview ? pageBandColor : 'grey.50'}
      >
        <InvoiceDetailPageHeader
          invoice={invoice}
          onBack={onBack}
          backLabel={backLabel}
          onOpenDocument={() => setDocumentOpen(true)}
          hasDocument={hasDocument}
          onEdit={canDecide ? () => setEditOpen(true) : null}
          editLabel={isFinal ? 'Edit stamp' : 'Edit'}
          aiReview={<AiReviewSummary invoice={invoice} autoGenerate={!isPreview} />}
          actionsWidth={actionsWidth}
          actions={
            credit && invoice ? (
              isPreview ? null : (
                <CreditAppliesToCard credit={invoice} />
              )
            ) : isMobile ? null : isPreview ? (
              (decisionCard ?? actions)
            ) : (
              <Stack spacing={2}>
                {decisionCard ?? actions}
                {invoice ? <InvoiceCreditsCard invoice={invoice} /> : null}
              </Stack>
            )
          }
          compact={isPreview}
          decision={
            isPreview ? (
              credit && invoice ? (
                <CreditAppliesToCard credit={invoice} />
              ) : (
                decisionCard
              )
            ) : null
          }
          footer={
            isPreview ? (
              <Stack spacing={2}>
                {invoice ? <InvoiceCreditsCard invoice={invoice} /> : null}
                <InvoiceDocumentViewer
                  primaryDocuments={primaryDocuments}
                  supportingDocuments={supportingDocuments}
                  isLoading={isLoading}
                  sx={{
                    ...(hasDocument && { height: '75vh' }),
                    ...CARD_SX,
                    bgcolor: 'background.paper',
                  }}
                />
              </Stack>
            ) : null
          }
        />

        {isMobile && (decisionCard || invoice?.credits.length) ? (
          <Stack px={2} py={2} spacing={2}>
            {decisionCard}
            {invoice ? <InvoiceCreditsCard invoice={invoice} /> : null}
          </Stack>
        ) : null}

        {isPreview ? null : (
          <Box display="flex" flex={1} minHeight={0} minWidth={0}>
            <InvoiceTabContainer invoice={invoice} tickets={tickets} disputeAgent={disputeAgent} />
          </Box>
        )}

        {/* Pinned across the bottom of the viewport on phones. The spacer keeps the bar from
            covering the last row of whichever tab is open. */}
        {isMobile && !decisionCard && canDecide && !credit ? (
          <>
            <Box sx={{ height: MOBILE_ACTION_BAR_HEIGHT }} aria-hidden />
            <Paper
              elevation={8}
              square
              sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: (theme) => theme.zIndex.appBar,
                px: 1,
              }}
            >
              {actions}
            </Paper>
          </>
        ) : null}

        {/* One or the other, never both: `editLabel` says which the button opened. */}
        {editOpen && !isFinal && invoiceFormData ? (
          <InvoiceDialog
            open
            onClose={closeEdit}
            onSubmit={closeEdit}
            isEdit
            kind={invoiceFormData.kind}
            defaultValues={invoiceFormData}
            overPage={isPreview}
          />
        ) : null}
        {editOpen && isFinal ? finalDecisionForm : null}

        {/* Mounted only while open: the dialog holds a loaded PDF. */}
        {documentOpen ? (
          <InvoiceDocumentDialog
            open
            onClose={() => setDocumentOpen(false)}
            primaryDocuments={primaryDocuments}
            supportingDocuments={supportingDocuments}
            isLoading={isLoading}
            title={
              invoice
                ? `${invoiceKindToLabelMap.get(invoice.kind)} #${invoice.number}`
                : 'Preview Document'
            }
          />
        ) : null}
      </Box>
    </DetailTableHeightProvider>
  );
}
